import React, {useState} from "react"
import {LoadingButton} from "@mui/lab"
import {Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField, Typography} from "@mui/material"
import {reviewVersion} from "./aiTicketsUtils"

const preview = (text, max = 400) => (text && text.length > max ? `${text.slice(0, max)}…` : text)

/** "Send this AI answer to the player?" — approve is irreversible, so it asks first. */
export const ApproveDialog = ({open, ticketId, answer, loading, onClose, onConfirm}) => (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="sm">
        <DialogTitle>Send AI answer to ticket #{ticketId}?</DialogTitle>
        <DialogContent>
            <DialogContentText component="div">
                The message below will be sent to the player in Zendesk. This cannot be undone.
            </DialogContentText>
            <Typography
                variant="body2"
                component="div"
                sx={{mt: 1.5, p: 1.5, bgcolor: "grey.100", borderRadius: 1, whiteSpace: "pre-wrap", wordBreak: "break-word"}}
            >
                {preview(answer?.text)}
            </Typography>
            {reviewVersion(answer) && (
                <>
                    <Typography variant="caption" color="text.secondary" component="div" sx={{mt: 1.5}}>
                        {reviewVersion(answer).label} (not sent, for your reference):
                    </Typography>
                    <Typography
                        variant="body2"
                        component="div"
                        color="text.secondary"
                        sx={{mt: 0.5, p: 1.5, borderRadius: 1, border: 1, borderColor: "divider", whiteSpace: "pre-wrap", wordBreak: "break-word"}}
                    >
                        {preview(reviewVersion(answer).text)}
                    </Typography>
                </>
            )}
        </DialogContent>
        <DialogActions>
            <Button onClick={onClose} variant="outlined" disabled={loading}>Cancel</Button>
            <LoadingButton onClick={onConfirm} color="success" variant="contained" loading={loading} autoFocus>
                Send to player
            </LoadingButton>
        </DialogActions>
    </Dialog>
)

/** Generic yes/no confirmation for destructive debug actions (deleting drafts). */
export const ConfirmDialog = ({open, title, text, confirmText, loading, onClose, onConfirm}) => (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="xs">
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
            <DialogContentText>{text}</DialogContentText>
        </DialogContent>
        <DialogActions>
            <Button onClick={onClose} variant="outlined" disabled={loading}>Cancel</Button>
            <LoadingButton onClick={onConfirm} color="error" variant="contained" loading={loading} autoFocus>
                {confirmText}
            </LoadingButton>
        </DialogActions>
    </Dialog>
)

/** Reject with an optional reason (stored as failReason in ai-zendesk). */
export const RejectDialog = ({open, ticketId, loading, onClose, onConfirm}) => {
    const [reason, setReason] = useState("")

    const close = () => {
        if (loading) return
        setReason("")
        onClose()
    }

    return (
        <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
            <DialogTitle>Reject AI answer for ticket #{ticketId}?</DialogTitle>
            <DialogContent>
                <DialogContentText>The answer will never be sent. A reason helps improve the bot.</DialogContentText>
                <TextField
                    autoFocus
                    fullWidth
                    multiline
                    minRows={2}
                    maxRows={5}
                    size="small"
                    label="Reason (optional)"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    sx={{mt: 2}}
                    disabled={loading}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={close} variant="outlined" disabled={loading}>Cancel</Button>
                <LoadingButton
                    onClick={() => onConfirm(reason.trim() || null)}
                    color="warning"
                    variant="contained"
                    loading={loading}
                >
                    Reject
                </LoadingButton>
            </DialogActions>
        </Dialog>
    )
}
